import produce from "immer"
import { IDLE } from "const"
import { InferValueTypes } from "client/utils"
import { initCcLibraryItem } from "client/store"
import {
    CcComplianceHistoryCreate,
    CcComplianceItemState,
} from "server/models"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initComplianceItemState: CcComplianceItemState = {
    itemId: null,
    complianceRevisionId: null,
    levelId: null,
    verifierId: null,
    settings: null,
}

export const initComplianceHistory: CcComplianceHistoryCreate = {
    itemId: null,
    complianceRevisionId: null,
    parentId: null,
    prevLevelId: null,
    prevUserId: null,
    nextLevelId: null,
    nextUserId: null,
    responseId: null,
    answer: null,
    remark: null,
    prevStatus: null,
    nextStatus: null,
    workflow: null,
    systemMessage: true,
    editorId: null,
}

const initCcComplianceState: types.CcComplianceState = {
    baseRoute: null,
    tableClass: "compliance-table",
    showColumnSettings: false,

    itemList: {},
    itemGroupedIds: {},

    setList: {},
    setIds: [],

    revisionList: {},
    revisionActiveList: {},

    itemStateList: {},
    itemHistoryList: {},
    tempAnswerList: {},

    selectedItemIds: [],
    visibleItemIds: [],

    listLoadState: IDLE,
    reloadList: true,

    // popup menu
    active: null,
    menuShow: false,
    menuTarget: null,

    // view history
    itemDetails: initCcLibraryItem,
    itemState: initComplianceItemState,
    itemHistory: [],
    tempAnswers: {},
    tempAnswerVersions: {},
    tempAnswerHistory: null,
    reloadAnswer: true,
}

const ccComplianceReducer = produce((
    draft: types.CcComplianceState = initCcComplianceState,
    action: ActionTypes = null,
): types.CcComplianceState => {
    switch (action.type) {
        case types.CC_COMPLIANCE_BASE_ROUTE:
            draft.baseRoute = action.baseRoute
            return draft

        case types.CC_COMPLIANCE_COLUMN_SETTINGS:
            draft.showColumnSettings = action.show
            return draft

        case types.CC_COMPLIANCE_ITEM_LIST:
            draft.itemList = {
                ...draft.itemList,
                ...action.itemList,
            }
            return draft

        case types.CC_COMPLIANCE_ITEM_GROUPED_IDS:
            draft.itemGroupedIds = {
                ...draft.itemGroupedIds,
                [action.setId]: action.groupedIds,
            }
            return draft

        case types.CC_COMPLIANCE_SET_LIST:
            draft.setList = action.setList
            return draft

        case types.CC_COMPLIANCE_SET_IDS:
            draft.setIds = action.setIds
            return draft

        case types.CC_COMPLIANCE_REVISION_LIST:
            draft.revisionList = {
                ...draft.revisionList,
                [action.setId]: action.revisions,
            }
            return draft

        case types.CC_COMPLIANCE_REVISION_ACTIVE_LIST:
            draft.revisionActiveList = {
                ...draft.revisionActiveList,
                [action.setId]: action.revision,
            }
            return draft

        case types.CC_COMPLIANCE_ITEM_STATE_LIST:
            draft.itemStateList = {
                ...draft.itemStateList,
                [action.setId]: action.itemStateList,
            }
            return draft

        case types.CC_COMPLIANCE_ITEM_HISTORY_LIST:
            draft.itemHistoryList = {
                ...draft.itemHistoryList,
                [action.setId]: action.itemHistoryList,
            }
            return draft

        case types.CC_COMPLIANCE_TEMP_ANSWER_LIST:
            draft.tempAnswerList = {
                ...draft.tempAnswerList,
                [action.setId]: action.tempAnswerList,
            }
            return draft

        case types.CC_COMPLIANCE_SELECTED_ITEM_IDS:
            draft.selectedItemIds = action.selected
            return draft

        case types.CC_COMPLIANCE_VISIBLE_ITEM_IDS:
            draft.visibleItemIds = action.visible
            return draft

        case types.CC_COMPLIANCE_LIST_LOAD_STATE:
            draft.listLoadState = action.state
            return draft

        case types.CC_COMPLIANCE_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        // popup menu
        case types.CC_COMPLIANCE_ACTIVE:
            draft.active = action.active
            return draft

        case types.CC_COMPLIANCE_POPUP_MENU_SHOW:
            draft.menuShow = action.show
            return draft

        case types.CC_COMPLIANCE_POPUP_MENU_TARGET:
            draft.menuTarget = action.target
            return draft

        // view history
        case types.CC_COMPLIANCE_ITEM_DETAILS:
            draft.itemDetails = action.details
            return draft

        case types.CC_COMPLIANCE_ITEM_STATE:
            draft.itemState = action.state
            return draft

        case types.CC_COMPLIANCE_ITEM_HISTORY:
            draft.itemHistory = action.history
            return draft

        case types.CC_COMPLIANCE_TEMP_ANSWERS:
            draft.tempAnswers = action.answers
            return draft

        case types.CC_COMPLIANCE_TEMP_ANSWER_VERSIONS:
            draft.tempAnswerVersions = action.versions
            return draft

        case types.CC_COMPLIANCE_TEMP_ANSWER_HISTORY:
            draft.tempAnswerHistory = action.history
            return draft

        case types.CC_COMPLIANCE_RELOAD_ANSWER:
            draft.reloadAnswer = action.value
            return draft

        default:
            return draft
    }
})
export default ccComplianceReducer
